import { Delete, ExpandMore } from '@mui/icons-material';
import { Button, Tooltip, Typography } from '@mui/material';
import {
    GridToolbarContainer,
    GridToolbarColumnsButton,
    GridToolbarDensitySelector,
    GridToolbarExport,
    GridToolbarFilterButton,
    useGridApiContext,
    GridApiPro
} from '@mui/x-data-grid-pro';
import { useRef, useState } from 'react';
import { ImportRecord } from 'ui-component/icons';
import { BasicMenuList } from 'ui-component/menus';
import { ImportLineItems } from 'ui-component/records/ImportDialogs';
import { SearchBar } from 'ui-component/SearchBar';

export type LineItemsCustomToolbarProps = {
    shouldEdit?: boolean;
    handleClickDelete?: () => void;
    shouldExport?: boolean;
    lineItemsApiRef?: React.MutableRefObject<GridApiPro>;
    recordTypeId: number;
    recordId: number;
    refetchGrid: () => void;
    baseIndexForTheCreation?: number;
    shouldImport?: boolean;
} & ({ manualCreation?: false } | { manualCreation: true; onSubmitCreation: (rows: Record<string, any>[]) => void });

export const LineItemsCustomToolbar = ({
    shouldEdit,
    shouldExport,
    handleClickDelete,
    lineItemsApiRef,
    recordTypeId,
    recordId,
    baseIndexForTheCreation,
    refetchGrid,
    shouldImport = true,
    ...props
}: LineItemsCustomToolbarProps) => {
    const apiRef = useGridApiContext();
    const inpuSifRef = useRef<HTMLInputElement | null>(null);

    const [searchText, setSearchText] = useState('');
    const [importAnchorRef, setImportAnchorRef] = useState<HTMLElement | null>(null);
    const [downloadTemplateAnchor, setDownloadTemplateAnchor] = useState<HTMLElement | null>(null);
    const [selectedSifFile, setSelectedSifFile] = useState<File | null>(null);
    const [isOpenImportDialog, setIsOpenImportDialog] = useState(false);

    const handleSearch = (event: React.ChangeEvent<HTMLInputElement>) => {
        const value = event.target.value;
        setSearchText(value);
        apiRef.current.setQuickFilterValues([value]);
    };

    return (
        <GridToolbarContainer sx={{ mb: 1 }}>
            <SearchBar value={searchText} onChange={handleSearch} placeholder="Find in current page" size="small" />
            <GridToolbarColumnsButton
                sx={({ palette }) => ({
                    color: palette.primary[400],
                    backgroundColor: palette.primary[300],
                    borderRadius: '30px',
                    padding: '4px 8px'
                })}
                endIcon={<ExpandMore />}
            />
            <GridToolbarFilterButton
                sx={({ palette }) => ({
                    color: palette.primary[400],
                    backgroundColor: palette.primary[300],
                    borderRadius: '30px',
                    padding: '4px 8px'
                })}
                componentsProps={{ button: { endIcon: <ExpandMore /> } }}
            />
            <GridToolbarDensitySelector
                sx={({ palette }) => ({
                    color: palette.primary[400],
                    backgroundColor: palette.primary[300],
                    borderRadius: '30px',
                    padding: '4px 8px'
                })}
                endIcon={<ExpandMore />}
            />
            {shouldExport && (
                <GridToolbarExport
                    sx={(theme: { palette: { primary: any[] } }) => ({
                        color: theme.palette.primary[400],
                        backgroundColor: theme.palette.primary[300],
                        borderRadius: '30px',
                        padding: '8px'
                    })}
                    endIcon={<ExpandMore />}
                />
            )}

            {shouldImport && (
                <>
                    <Tooltip title="Import">
                        <Button
                            color="primary"
                            onClick={(e) => {
                                e.preventDefault();
                                setImportAnchorRef(e.currentTarget);
                            }}
                            sx={({ palette }) => ({
                                color: palette.primary[400],
                                backgroundColor: palette.primary[300],
                                borderRadius: '30px',
                                padding: '5px 8px'
                            })}
                        >
                            <ImportRecord sx={{ fontSize: '18px' }} />
                            <Typography
                                variant="body1"
                                sx={{ fontSize: '0.8125rem', fontWeight: '500', color: '#858585', marginLeft: '4px' }}
                                component="div"
                            >
                                Import
                            </Typography>
                            <ExpandMore fontSize="small" sx={{ ml: '6px' }} />
                        </Button>
                    </Tooltip>

                    <BasicMenuList
                        key="line-items-import-menu"
                        open={!!importAnchorRef}
                        anchorEl={importAnchorRef}
                        onClose={() => setImportAnchorRef(null)}
                        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                        items={[
                            { text: 'Import', onClick: () => inpuSifRef.current?.click() }
                            // { text: 'Import of Catalog', onClick: () => {} },
                            // {
                            //     text: 'Download template',
                            //     noAutoClose: true,
                            //     showChevron: true,
                            //     onClick: (e) => setDownloadTemplateAnchor(e?.currentTarget || null)
                            // }
                        ]}
                    />
                    <BasicMenuList
                        key="line-items-download-template-menu"
                        paperSx={{ width: downloadTemplateAnchor?.clientWidth || 'auto' }}
                        open={!!downloadTemplateAnchor}
                        anchorEl={downloadTemplateAnchor}
                        onClose={() => setDownloadTemplateAnchor(null)}
                        anchorOrigin={{ vertical: 'center', horizontal: 'right' }}
                        transformOrigin={{ vertical: 'center', horizontal: 'left' }}
                        items={[{ text: 'SIF' }]}
                    />
                    <input
                        type="file"
                        ref={inpuSifRef}
                        name="lineItemsSifFile"
                        id="lineItemsSifFileInput"
                        accept=".sif"
                        onChange={(event) => {
                            const file = event.target.files?.[0];
                            if (file) {
                                setSelectedSifFile(event.target.files?.[0] || null);
                                setIsOpenImportDialog(true);
                            }
                        }}
                        style={{ display: 'none' }}
                    />
                    {isOpenImportDialog && (
                        <ImportLineItems
                            open={isOpenImportDialog}
                            onClose={() => {
                                setIsOpenImportDialog(false);
                                if (inpuSifRef.current) inpuSifRef.current.value = '';
                                setSelectedSifFile(null);
                            }}
                            sifFile={selectedSifFile}
                            apiRef={lineItemsApiRef}
                            recordTypeId={recordTypeId}
                            recordId={recordId}
                            baseIndexForTheCreation={baseIndexForTheCreation}
                            manualCreation={props.manualCreation}
                            onSubmitCreation={props.manualCreation ? props.onSubmitCreation : undefined}
                            onSubmit={() => {
                                setIsOpenImportDialog(false);
                                if (inpuSifRef.current) inpuSifRef.current.value = '';
                                refetchGrid();
                            }}
                        />
                    )}
                </>
            )}
            {handleClickDelete && (
                <Button
                    onClick={handleClickDelete}
                    sx={{
                        color: 'white',
                        backgroundColor: '#EF5350',
                        borderRadius: '30px',
                        padding: '8px',
                        '&:hover': { bgcolor: '#bf423f' }
                    }}
                    startIcon={<Delete />}
                    size="medium"
                    color="primary"
                >
                    Delete
                </Button>
            )}
        </GridToolbarContainer>
    );
};
